import React, { useState } from 'react';
import NoSSR from 'react-no-ssr';
import Slider from 'react-slick';

import { useIsDesktop } from '@common/hooks/useIsDesktop';
import ImgixWrapper from '@common/components/ImgixWrapper';
import { gridTheme } from '@utils/grid';
import { Image } from 'shared-types/Common';

import {
  StyledProductBoxImage,
  ImageButton,
  ImageColumn,
  ImagesList,
  SliderWrapper,
} from './ImagePreview.styles';

export type ImagePreviewProps = {
  images: Array<Image>;
  altLabel: string;
};

export const ImagePreview: React.FC<ImagePreviewProps> = ({ images, altLabel }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const isDesktop = useIsDesktop();

  const settings = {
    thumbnailImageSize: isDesktop ? 140 : 120,
  };

  const hasImages = images?.length > 0;

  return (
    <ImageColumn data-cy="image-column">
      <StyledProductBoxImage data-cy="product-box-image" isPlaceholder={!hasImages}>
        {hasImages && (
          <ImgixWrapper
            data-cy="imgix-wrapper"
            src={images[selectedImageIndex].url}
            sizes={`(max-width: ${gridTheme.breakpoints.xl}px) 350px, 587px`}
            imgixParams={{
              'max-h': 700,
              'min-h': 200,
            }}
            alt={altLabel}
          />
        )}
      </StyledProductBoxImage>
      {(images?.length === 2 || images?.length === 3) && (
        <ImagesList data-cy="images-list">
          {images.map((image, index) => (
            <li key={image.externalKey}>
              <ImageButton
                data-cy="image-button"
                onClick={() => setSelectedImageIndex(index)}
                isSelected={index === selectedImageIndex}
              >
                <ImgixWrapper
                  data-cy="imgix-wrapper"
                  src={image.url}
                  width={settings.thumbnailImageSize}
                  height={settings.thumbnailImageSize}
                  alt={altLabel}
                />
              </ImageButton>
            </li>
          ))}
        </ImagesList>
      )}
      {images && images.length > 3 && (
        <NoSSR>
          <SliderWrapper data-cy="slider-wrapper">
            <Slider
              data-cy="slider"
              speed={300}
              variableWidth
              arrows={false}
              beforeChange={() => setIsDragging(true)}
              afterChange={() => setIsDragging(false)}
            >
              {images.map((image, index) => (
                <ImageButton
                  data-cy="image-button"
                  key={image.externalKey}
                  isSelected={index === selectedImageIndex}
                  onClick={(event) => {
                    if (isDragging) {
                      event.preventDefault();
                    }
                    setSelectedImageIndex(index);
                  }}
                >
                  <ImgixWrapper
                    data-cy="imgix-wrapper"
                    src={image.url}
                    width={settings.thumbnailImageSize}
                    height={settings.thumbnailImageSize}
                    alt={altLabel}
                  />
                </ImageButton>
              ))}
            </Slider>
          </SliderWrapper>
        </NoSSR>
      )}
    </ImageColumn>
  );
};
